import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  LongServiceLeaveView,
  parseApplicantFromContent,
} from '@/components/Approval/@components/LongServiceLeave';
import { useFindPassport } from '@/hooks/apiHooks';
import useApprovalView from '@/hooks/approval/useApprovalView';
import { normalizeLang, pickI18nText } from '@/utils/approval/i18nText';
import {
  parseLongServiceLeaveContent,
  parseLongServiceLeaveData,
} from '@/utils/approval/longServiceLeave';
import { formatAsDate } from '@/utils/format';
import type { ContentExtraProps } from '../../types';
import { ContentSlotRoot } from '../styles';

/**
 * 240 F&CO 장기근속 포상휴가 — 신청자 정보는 저장 content 의 작성 시점 값(없으면 작성자 지금 값),
 * 포상 내용·기간·서약 동의는 저장 data
 */
const LongServiceLeaveSlot = ({ data }: ContentExtraProps) => {
  const { i18n } = useTranslation();
  const lang = normalizeLang(i18n.language);
  const content = parseLongServiceLeaveContent(data.attributes);
  const saved = parseLongServiceLeaveData(data.attributes);
  const snapshot = useMemo(() => parseApplicantFromContent(content), [content]);
  const { data: view } = useApprovalView(snapshot ? '' : data.documentId);
  const author = view?.author;
  const { data: passport } = useFindPassport(
    snapshot ? '' : author?.passportId ?? '',
  );
  if (!saved) return null;

  const applicant = snapshot ?? {
    name: author ? pickI18nText(author.name, lang) : '',
    employeeNumber: author?.employeeNumber ?? '',
    affiliation: author
      ? [
          pickI18nText(author.companyName, lang),
          pickI18nText(author.divisionName, lang),
          author.departmentName,
        ]
          .filter(Boolean)
          .join(' ')
      : '',
    enterDate: passport?.detail?.joinedAt
      ? formatAsDate(passport.detail.joinedAt)
      : '',
  };

  return (
    <ContentSlotRoot>
      <LongServiceLeaveView applicant={applicant} data={saved} />
    </ContentSlotRoot>
  );
};

export default LongServiceLeaveSlot;
